import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Mic,
  Paperclip,
  Loader2,
  CheckCircle2,
  CloudUpload,
  AlertCircle,
  FileText,
  Image as ImageIcon,
  Info,
  RotateCcw,
} from 'lucide-react';
import { Button } from './ui/Button';
import { type MediaTipo } from '../lib/media';
import { nuevoIdem } from '../lib/colaOffline';
import { useColaMedia } from '../lib/colaMedia';
import { borrarGrabacion } from '../lib/grabacionEnCurso';
import { PulsarParaHablar } from './PulsarParaHablar';
import type { Transcripcion } from '../lib/sesion';

/**
 * "Levantá evidencia" (hito M2) — bloque del Bolsillo para capturar prueba en
 * territorio: una nota de voz (que se transcribe sola en el worker del host) o
 * una foto / archivo adjunto. Todo se sube PROXIADO por la API a MinIO.
 *
 * Cada envío es idempotente (token `idem`): si se cae la red, la subida queda
 * "En cola" y se reintenta sola al volver la conexión con el MISMO token, sin
 * duplicar el aporte (C1). No jerga: copy claro para vecinos.
 */

type EstadoEvidencia = 'subiendo' | 'en_cola' | 'enviado' | 'error';

interface Evidencia {
  key: string;
  idem: string;
  tipo: MediaTipo;
  filename: string;
  mime: string;
  blob: Blob;
  /** ISO del arranque y del final de la grabación (ordena y sella el acta). */
  inicio: string;
  fin: string;
  duracionS: number;
  estado: EstadoEvidencia;
  aporteId: string | null;
  error: string | null;
}

/** Deriva el tipo de evidencia del MIME del archivo elegido. */
function tipoDeArchivo(mime: string): MediaTipo {
  if (mime.startsWith('image/')) return 'foto';
  if (mime.startsWith('audio/')) return 'audio';
  return 'archivo';
}

export function LevantaEvidencia({
  actividadId,
  transcripciones,
  soloAdjuntos = false,
}: {
  actividadId: string;
  /** Mapa aporte_id -> transcripción, alimentado por WS desde useSalaViva. */
  transcripciones: Record<string, Transcripcion>;
  /**
   * true cuando la herramienta tiene VOZ EN FORMATO (captura de texto): el
   * grabador vive DENTRO del formulario llevando la categoría elegida, y acá
   * queda solo el adjunto de foto/archivo — dos grabadores confunden, y el
   * suelto pierde la categoría (bug real de la demo del 1-ago).
   */
  soloAdjuntos?: boolean;
}) {
  const [items, setItems] = useState<Evidencia[]>([]);
  const itemsRef = useRef<Evidencia[]>([]);
  itemsRef.current = items;
  const fileRef = useRef<HTMLInputElement>(null);

  const patch = useCallback((key: string, cambios: Partial<Evidencia>) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...cambios } : it)));
  }, []);

  /**
   * Todo pasa por la COLA DURABLE: el blob se guarda en IndexedDB antes de
   * tocar la red, así que la grabación está a salvo aunque se recargue la
   * página o el navegador mate la pestaña. Acá solo reflejamos el resultado;
   * el reintento lo maneja la cola sola (al volver la red, al volver al
   * frente, y al abrir la app).
   */
  const subir = useCallback(
    async (ev: Evidencia) => {
      patch(ev.key, { estado: 'subiendo', error: null });
      const entregado = await useColaMedia.getState().encolarYEnviar({
        idem: ev.idem,
        actividadId,
        tipo: ev.tipo,
        blob: ev.blob,
        filename: ev.filename,
        mime: ev.mime,
        inicio: ev.inicio,
        fin: ev.fin,
        duracionS: ev.duracionS,
      });
      // Ya está a salvo en la cola de envío: los trozos pueden liberarse.
      void borrarGrabacion(ev.idem);
      if (entregado) {
        patch(ev.key, { estado: 'enviado', error: null });
        return;
      }
      const enCola = useColaMedia.getState().items.find((i) => i.idem === ev.idem);
      patch(ev.key, {
        estado: enCola?.bloqueado ? 'error' : 'en_cola',
        error: enCola?.bloqueado ?? null,
      });
    },
    [actividadId, patch],
  );

  const encolar = useCallback(
    (
      blob: Blob,
      tipo: MediaTipo,
      filename: string,
      mime: string,
      tiempos?: { inicio: string; fin: string; duracionS: number; id?: string },
    ) => {
      const ahora = new Date().toISOString();
      const ev: Evidencia = {
        key: nuevoIdem(),
        // El idem ES el id de la grabación: si esta pestaña muere antes de
        // limpiar los trozos, la recuperación reenvía con el MISMO token y el
        // backend deduplica en vez de crear un aporte repetido (C1).
        idem: tiempos?.id ?? nuevoIdem(),
        tipo,
        filename,
        mime,
        blob,
        inicio: tiempos?.inicio ?? ahora,
        fin: tiempos?.fin ?? ahora,
        duracionS: tiempos?.duracionS ?? 0,
        estado: 'subiendo',
        aporteId: null,
        error: null,
      };
      setItems((prev) => [ev, ...prev]);
      void subir(ev);
    },
    [subir],
  );

  // El reintento lo maneja la cola durable (online / volver al frente / abrir
  // la app). Acá solo espejamos: lo que ya salió de la cola, se entregó.
  const pendientesCola = useColaMedia((s) => s.items);
  useEffect(() => {
    for (const it of itemsRef.current) {
      if (it.estado !== 'en_cola') continue;
      const sigue = pendientesCola.find((p) => p.idem === it.idem);
      if (!sigue) patch(it.key, { estado: 'enviado', error: null });
      else if (sigue.bloqueado) patch(it.key, { estado: 'error', error: sigue.bloqueado });
    }
  }, [pendientesCola, patch]);

  function onArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      encolar(file, tipoDeArchivo(file.type), file.name, file.type || 'application/octet-stream');
    }
    // Permite volver a elegir el mismo archivo.
    if (fileRef.current) fileRef.current.value = '';
  }

  return (
    <section className="mt-6 rounded-lg border border-border bg-surface-raised px-4 py-4">
      <h2 className="flex items-center gap-2 font-display text-base font-semibold text-content">
        {soloAdjuntos ? (
          <Paperclip className="h-5 w-5 text-cyan" aria-hidden="true" />
        ) : (
          <Mic className="h-5 w-5 text-cyan" aria-hidden="true" />
        )}
        {soloAdjuntos ? 'Adjuntá evidencia' : 'Aportá con tu voz'}
      </h2>
      <p className="mt-1 font-sans text-sm text-content-muted">
        {soloAdjuntos
          ? 'Una foto del lugar o un documento también cuentan. Quedan en el acta de la sesión con tu nombre.'
          : 'Mantené presionado el botón y hablá. También podés adjuntar una foto o un documento. Todo queda en el acta de la sesión con tu nombre.'}
      </p>

      {!soloAdjuntos && (
        <PulsarParaHablar
          actividadId={actividadId}
          onListo={(g) =>
            encolar(g.blob, 'audio', `nota-de-voz.${g.ext}`, g.mime, {
              inicio: g.inicioISO,
              fin: g.finISO,
              duracionS: g.duracionS,
              id: g.id,
            })
          }
        />
      )}

      <div className="mt-3">
        <input
          ref={fileRef}
          type="file"
          accept="image/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
          onChange={onArchivo}
          className="sr-only"
          id="evidencia-archivo"
        />
        <label
          htmlFor="evidencia-archivo"
          className="flex min-h-touch w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-border-strong bg-surface-base px-4 py-3 font-sans text-base font-semibold text-content transition-colors duration-fast hover:bg-surface-sunken focus-within:shadow-focus"
        >
          <Paperclip className="h-5 w-5 text-primary" aria-hidden="true" />
          Adjuntar foto o archivo
        </label>
      </div>

      {items.length > 0 && (
        <ul className="mt-4 flex flex-col gap-2">
          {items.map((it) => (
            <FilaEvidencia
              key={it.key}
              ev={it}
              transcripcion={it.aporteId ? transcripciones[it.aporteId] ?? null : null}
              onReintentar={() => subir(it)}
              onQuitar={() => setItems((prev) => prev.filter((x) => x.key !== it.key))}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/* ---------------------------- Fila de estado --------------------------- */

function FilaEvidencia({
  ev,
  transcripcion,
  onReintentar,
  onQuitar,
}: {
  ev: Evidencia;
  transcripcion: Transcripcion | null;
  onReintentar: () => void;
  onQuitar: () => void;
}) {
  const Icono = ev.tipo === 'audio' ? Mic : ev.tipo === 'foto' ? ImageIcon : FileText;

  return (
    <li className="rounded-md border border-border bg-surface-base px-3 py-2.5">
      <div className="flex items-center gap-2">
        <Icono className="h-4 w-4 shrink-0 text-content-muted" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate font-sans text-sm text-content">
          {ev.filename}
        </span>
        <EstadoChip ev={ev} />
      </div>

      {/* Transcripción del audio, cuando llega por WS. */}
      {ev.tipo === 'audio' && ev.estado === 'enviado' && (
        <div className="mt-2 rounded-md border border-violet px-3 py-2">
          {transcripcion ? (
            <p className="flex items-start gap-2 font-sans text-sm text-content">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-violet" aria-hidden="true" />
              <span>{transcripcion.texto}</span>
            </p>
          ) : (
            <p className="flex items-center gap-2 font-sans text-sm text-violet">
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
              Enviado — transcribiendo…
            </p>
          )}
        </div>
      )}

      {ev.estado === 'error' && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="flex items-center gap-1.5 font-sans text-sm text-error">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            {ev.error ?? 'No se pudo subir.'}
          </p>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={onReintentar} leadingIcon={<RotateCcw className="h-4 w-4" aria-hidden="true" />}>
              Reintentar
            </Button>
            <Button variant="ghost" size="sm" onClick={onQuitar}>
              Quitar
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

function EstadoChip({ ev }: { ev: Evidencia }) {
  if (ev.estado === 'subiendo') {
    return (
      <span className="flex items-center gap-1 font-sans text-xs font-semibold text-primary">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        Subiendo…
      </span>
    );
  }
  if (ev.estado === 'en_cola') {
    return (
      <span className="flex items-center gap-1 font-sans text-xs font-semibold text-warning">
        <CloudUpload className="h-3.5 w-3.5" aria-hidden="true" />
        En cola
      </span>
    );
  }
  if (ev.estado === 'enviado') {
    return (
      <span className="flex items-center gap-1 font-sans text-xs font-semibold text-success">
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
        Enviado
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 font-sans text-xs font-semibold text-error">
      <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
      Falló
    </span>
  );
}
